<template>
  <v-card :class="['custom-color-picker', themeClass]" flat>
    <!-- Header -->
    <div class="picker-header">
      <h2 class="picker-title">{{ pickerTitle }}</h2>
      <div class="picker-mode">{{ modeLabel }}</div>
    </div>

    <!-- Suggested colors -->
    <template v-if="pickerVariant === 'background'">
      <div class="picker-section">
        <div class="section-title">{{ suggestedTitle }}</div>

        <div class="swatches-row">
          <button
            v-for="colorItem in normalizedSuggestedColors"
            :key="colorItem"
            type="button"
            class="swatch-btn"
            :class="{ active: localColor === colorItem }"
            :style="{ backgroundColor: colorItem }"
            @click="selectColor(colorItem)"
          />
        </div>
      </div>
    </template>

    <!-- COLOR VARIANT -->
    <template v-else>
      <div class="picker-color-section">   
        <v-color-picker
          v-model="localColor"
          class="custom-v-color-picker custom-v-color-picker--slider-only"
          flat
          hide-mode-switch
          mode="hex"
        />
      </div>
       <div  class="selected-color-bar" :style="{ backgroundColor: localColor }"></div>
    </template>

    <!-- Hex input -->
    <div class="hex-row">
      <div class="hex-label">Hex</div>

      <input
        v-model="hexInput"
        class="hex-input"
        type="text"
        maxlength="7"
        placeholder="#000000"
        @input="handleHexInput"
      />
    </div>

    <!-- Footer actions -->
    <div class="picker-actions">
      <v-btn
        color="secondary"
        class="action-btn cancel-btn"
        depressed
        @click="close"
      >
        {{ cancelText }}
      </v-btn>

      <v-btn
        class="action-btn continue-btn"
        depressed
        :disabled="!isValidHex(hexInput)"
        @click="saveColor"
      >
        {{ confirmText }}
        <v-icon right small>fa-check</v-icon>
      </v-btn>
    </div>
  </v-card>
</template>

<script>
export default {
  name: "CustomColorPicker",
  props: {
    initialColor: {
      type: String,
      default: "#000000",
    },
    pickerTitle: {
      type: String,
      default: "Background Color",
    },
    modeLabel: {
      type: String,
      default: "Light Mode",
    },
    suggestedTitle: {
      type: String,
      default: "Suggested Colors",
    },
    cancelText: {
      type: String,
      default: "Cancel",
    },
    confirmText: {
      type: String,
      default: "Continuar",
    },
    suggestedColors: {
      type: Array,
      default: () => ["#FFFFFF", "#F5F5F5", "#E0E0E0", "#BDBDBD", "#000000"],
    },
    themeMode: {
      type: String,
      default: "light",
      validator: (value) => ["light", "dark"].includes(value),
    },
    pickerVariant: {
      type: String,
      default: "background",
      validator: (value) => ["background", "color"].includes(value),
    },
  },
  data() {
    return {
      localColor: "#000000",
      hexInput: "#000000",
    };
  },
  computed: {
    normalizedSuggestedColors() {
      return this.suggestedColors.map((color) => this.normalizeColor(color));
    },
    themeClass() {
      return `custom-color-picker--${this.themeMode}`;
    },
  },
  watch: {
    initialColor: {
      immediate: true,
      handler(newValue) {
        const normalized = this.normalizeColor(newValue);
        this.localColor = normalized;
        this.hexInput = normalized;
      },
    },
    localColor(newValue) {
      const normalized = this.normalizeColor(newValue);

      if (normalized !== this.hexInput) {
        this.hexInput = normalized;
      }

      if (normalized !== newValue) {
        this.localColor = normalized;
      }
    },
  },
  methods: {
    normalizeColor(value) {
      if (!value) return "#000000";

      let color = String(value).trim().replace(/^#/, "").toUpperCase();

      // soporta #FFF
      if (/^[0-9A-F]{3}$/.test(color)) {
        color = color
          .split("")
          .map((char) => char + char)
          .join("");
      }

      if (!/^[0-9A-F]{6}$/.test(color)) {
        return "#000000";
      }

      return `#${color}`;
    },

    isValidHex(value) {
      return /^#([0-9A-Fa-f]{6})$/.test(String(value || "").trim());
    },

    handleHexInput() {
      let value = String(this.hexInput || "")
        .trim()
        .replace(/\s/g, "");

      if (value && value[0] !== "#") {
        value = `#${value}`;
      }

      this.hexInput = value.toUpperCase();

      if (this.isValidHex(this.hexInput)) {
        this.localColor = this.hexInput;
      }
    },

    selectColor(color) {
      const normalized = this.normalizeColor(color);
      this.localColor = normalized;
      this.hexInput = normalized;
    },

    saveColor() {
      if (!this.isValidHex(this.hexInput)) return;

      // Mantengo el mismo contrato que tu componente actual:
      // emite el color con #
      this.$emit("saveColor", this.localColor);
    },

    close() {
      this.$emit("closeWindow");
    },
  },
};
</script>

<style scoped>
.custom-color-picker {
  width: 356px;
  padding: 16px;
  border-radius: 8px;
}

/* DARK */
.custom-color-picker--dark {
  background: #14161b;
}

.custom-color-picker--dark .picker-title {
  color: #fff !important;
}

.custom-color-picker--dark .section-title {
  color: #fff !important;
}

.custom-color-picker--dark .swatch-btn {
  border: 1px solid #fff;
}

.custom-color-picker--dark .hex-label {
  border: 1px solid #ababab;
  color: #ababab;
}

.picker-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 12px;
}

.picker-title {
  font-family: "Avenir" !important;
  font-size: 14px !important;
  font-weight: 700;
  color: #14161b;
}

.picker-mode {
  font-family: "Avenir" !important;
  font-size: 14px !important;
  letter-spacing: 2%;
  font-weight: 600;
  color: #ababab !important;
}

.picker-section {
  margin-bottom: 12px;
}

.section-title {
  font-size: 14px !important;
  font-weight: 600;
  letter-spacing: 2%;
  color: #3b3b3b;
  margin-bottom: 10px;
}

.swatches-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.swatch-btn {
  width: 24px;
  height: 24px;
  border-radius: 50%;
  border: 1px solid #ababab;
}

.swatch-btn.active {
  border: 1px solid #28ea96;
}

.hex-row {
  display: flex;
  align-items: stretch;
  gap: 8px;
  width: 100%;
  margin-bottom: 26px;
}

.hex-label {
  min-width: 58px;
  min-height: 35px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 1px solid #3b3b3b;
  border-radius: 4px;
  font-size: 14px;
  font-weight: 600;
  color: #3b3b3b;
}

.hex-input {
  min-width: 258px;
  min-height: 35px;
  border: 1px solid #ababab;
  border-radius: 4px;
  padding: 8px 16px;
  font-size: 14px;
  color: #ababab;
}

.picker-actions {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
}

.action-btn {
  flex: 1;
  border-radius: 18px;
  text-transform: none;
  letter-spacing: 0;
  font-size: 18px !important;
  font-weight: 700;
}


.picker-color-section {
  display: flex;
  justify-content: center;
  margin-bottom: 12px;
  width: 100% !important;
}

.selected-color-bar {
  width: 100%;
  height: 20px;
  border-radius: 4px;
  margin: 12px 0;
  border: 1px solid #ababab;
}

.custom-v-color-picker {
  box-shadow: none !important;
  width: 100% !important;
}

/* LIGHT */
.custom-color-picker--light .custom-v-color-picker {
  background: transparent !important;
}

/* DARK */
.custom-color-picker--dark .custom-v-color-picker {
  background: transparent !important;
}

.custom-color-picker--dark .selected-color-bar {
  border: 1px solid #ffffff;
}

::v-deep .custom-v-color-picker--slider-only .v-color-picker__dot {
  display: none !important;
}

::v-deep .v-color-picker__controls{
  padding: 16px 0;
}

</style>
